"use client";

import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { FileText, Calculator, Loader2, Eye, RefreshCw, Download, FileSpreadsheet } from "lucide-react";
import {
  fetchSalarySlips as fetchTeacherSalarySlips,
  calculateSalary as calculateTeacherSalary,
  fetchSalarySlipById as fetchTeacherSalarySlipById,
  updateSlipStatus as updateTeacherSlipStatus,
} from "../../../services/teacherSalaryService";
import {
  fetchSalarySlips as fetchStaffSalarySlips,
  calculateSalary as calculateStaffSalary,
  fetchSalarySlipById as fetchStaffSalarySlipById,
  updateSlipStatus as updateStaffSlipStatus,
} from "../../../services/staffSalaryService";
import { API_ENDPOINTS } from "../../../constants/config";
import { getFullName } from "../../../utils/urlUtils";
import { toast } from "react-hot-toast";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function PayrollSlipsContent({ employeeType: employeeTypeProp } = {}) {
  const outletContext = useOutletContext();
  const employeeType = employeeTypeProp || outletContext?.employeeType || "teacher";
  const isStaff = employeeType === "staff";
  const employeeLabel = isStaff ? "Staff" : "Teacher";
  const now = new Date();
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [year, setYear] = useState(String(now.getFullYear()));
  const [slips, setSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calcLoading, setCalcLoading] = useState(false);
  const [viewSlip, setViewSlip] = useState(null);
  const [slipDetail, setSlipDetail] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);

  const loadSlips = async () => {
    setLoading(true);
    try {
      const res = await (isStaff ? fetchStaffSalarySlips : fetchTeacherSalarySlips)(month, year);
      if (res.success) setSlips(res.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load slips");
      setSlips([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSlips();
  }, [month, year, employeeType]);

  const handleCalculate = async () => {
    setCalcLoading(true);
    try {
      const res = await (isStaff ? calculateStaffSalary : calculateTeacherSalary)(Number(month), Number(year));
      if (res.success) {
        toast.success("Salary calculation completed");
        loadSlips();
      } else {
        toast.error(res.message || "Calculation failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to calculate");
    } finally {
      setCalcLoading(false);
    }
  };

  const openViewSlip = async (slip) => {
    setViewSlip(slip);
    try {
      const res = await (isStaff ? fetchStaffSalarySlipById : fetchTeacherSalarySlipById)(slip._id);
      if (res.success) setSlipDetail(res.data);
      else setSlipDetail(slip);
    } catch (e) {
      setSlipDetail(slip);
    }
  };

  const handlePayslipDownload = async (slipId) => {
    setDownloadingId(slipId);
    try {
      const token = localStorage.getItem("token");
      const url = isStaff ? API_ENDPOINTS.staffSalarySlipDownload(slipId) : API_ENDPOINTS.teacherSalarySlipDownload(slipId);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition");
      const match = disposition && disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `payslip-${slipId}.xlsx`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Payslip downloaded");
    } catch (e) {
      toast.error(e.message || "Failed to download payslip");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadExcel = async () => {
    setExcelLoading(true);
    try {
      const token = localStorage.getItem("token");
      const url = isStaff ? API_ENDPOINTS.staffSalarySlipsExcel(month, year) : API_ENDPOINTS.teacherSalarySlipsExcel(month, year);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
      const filename = `salary-detail-${monthNames[Number(month) - 1]}-${year}.xlsx`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error(e.message || "Failed to download Excel");
    } finally {
      setExcelLoading(false);
    }
  };

  const updateStatus = async (slipId, status) => {
    setStatusLoading(true);
    try {
      await (isStaff ? updateStaffSlipStatus : updateTeacherSlipStatus)(slipId, status);
      toast.success("Status updated");
      if (viewSlip?._id === slipId) setViewSlip((s) => (s ? { ...s, status } : null));
      if (slipDetail?._id === slipId) setSlipDetail((d) => (d ? { ...d, status } : null));
      loadSlips();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to update");
    } finally {
      setStatusLoading(false);
    }
  };

  const formatMoney = (n) => (n != null ? `₹${Number(n).toLocaleString("en-IN")}` : "—");

  const statusVariant = (s) => {
    if (s === "paid") return "bg-emerald-100 text-emerald-800";
    if (s === "locked") return "bg-amber-100 text-amber-800";
    return "bg-slate-100 text-slate-700";
  };

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 5; y--) years.push(y);

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg rounded-xl overflow-hidden">
        <CardHeader className="bg-slate-50 border-b border-slate-200/80">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="flex items-center gap-2 text-slate-800 text-xl font-semibold">
              <FileText className="h-5 w-5 text-[#0263cc]" />
              Payslip
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger className="w-[140px] bg-white">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => (
                    <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger className="w-[100px] bg-white">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={handleCalculate}
                disabled={calcLoading}
                className="bg-[#0263cc] hover:bg-[#0255b0] text-white"
              >
                {calcLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Calculator className="h-4 w-4 mr-2" />}
                Generate Payslips
              </Button>
              <Button
                variant="outline"
                onClick={handleDownloadExcel}
                disabled={excelLoading || slips.length === 0}
                className="border-[#0263cc]/30 text-[#0263cc] hover:bg-[#0263cc]/10"
              >
                {excelLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
                Download Excel
              </Button>
              <Button variant="outline" size="icon" onClick={loadSlips} disabled={loading}>
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Generate payslips for the month, view or download individual slips, or export full list to Excel.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0263cc]" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="font-semibold">{employeeLabel}</TableHead>
                  <TableHead className="font-semibold">Employee ID</TableHead>
                  <TableHead className="font-semibold">Working Days</TableHead>
                  <TableHead className="font-semibold">Present</TableHead>
                  <TableHead className="font-semibold">Deduction Days</TableHead>
                  <TableHead className="font-semibold">Leave Deduction</TableHead>
                  <TableHead className="font-semibold">Net Salary</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {slips.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-10 text-slate-500">
                      No slips for this period. Click &quot;Calculate Salary&quot; to generate.
                    </TableCell>
                  </TableRow>
                ) : (
                  slips.map((row) => {
                    const employee = isStaff ? row.staff : row.teacher;
                    const name = employee ? getFullName(employee) : "—";
                    const empCode = isStaff ? employee?.staffId ?? "—" : employee?.employeeCode ?? "—";
                    return (
                      <TableRow key={row._id} className="hover:bg-slate-50/50">
                        <TableCell className="font-medium">{name}</TableCell>
                        <TableCell>{empCode}</TableCell>
                        <TableCell>{row.workingDays ?? "—"}</TableCell>
                        <TableCell>{row.presentDays ?? "—"}</TableCell>
                        <TableCell>{row.deductionDays ?? "—"}</TableCell>
                        <TableCell>{formatMoney(row.leaveDeduction)}</TableCell>
                        <TableCell className="font-medium">{formatMoney(row.netSalary)}</TableCell>
                        <TableCell>
                          <Badge className={statusVariant(row.status)}>{row.status}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openViewSlip(row)} className="text-[#0263cc]">
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePayslipDownload(row._id)}
                              disabled={downloadingId === row._id}
                              className="text-[#0263cc]"
                            >
                              {downloadingId === row._id ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
                              Download
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!viewSlip} onOpenChange={(o) => { if (!o) { setViewSlip(null); setSlipDetail(null); } }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#0263cc]">Salary Slip</DialogTitle>
          </DialogHeader>
          {slipDetail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <span className="text-slate-500">{employeeLabel}</span>
                <span className="font-medium">{isStaff ? (slipDetail.staff ? getFullName(slipDetail.staff) : "—") : (slipDetail.teacher ? getFullName(slipDetail.teacher) : "—")}</span>
                <span className="text-slate-500">Employee ID</span>
                <span>{isStaff ? (slipDetail.staff?.staffId ?? "—") : (slipDetail.teacher?.employeeCode ?? "—")}</span>
                <span className="text-slate-500">Period</span>
                <span>{MONTHS[(slipDetail.month || 1) - 1]} {slipDetail.year}</span>
                <span className="text-slate-500">Working Days</span>
                <span>{slipDetail.workingDays}</span>
                <span className="text-slate-500">Present</span>
                <span>{slipDetail.presentDays}</span>
                <span className="text-slate-500">Half Days</span>
                <span>{slipDetail.halfDays}</span>
                <span className="text-slate-500">Absent</span>
                <span>{slipDetail.absentDays ?? 0}</span>
                <span className="text-slate-500">Deduction Days</span>
                <span>{slipDetail.deductionDays}</span>
              </div>
              <div className="border-t pt-3 space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-500">Basic Salary</span>
                  <span>{formatMoney(slipDetail.basicSalary)}</span>
                </div>
                {slipDetail.breakdown?.allowanceDetails?.length > 0 ? (
                  slipDetail.breakdown.allowanceDetails.map((a, i) => (
                    <div key={i} className="flex justify-between text-slate-600">
                      <span>{a.headName} ({a.code})</span>
                      <span>{formatMoney(a.amount)}</span>
                    </div>
                  ))
                ) : (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Allowances</span>
                    <span>{formatMoney(slipDetail.allowances)}</span>
                  </div>
                )}
                <div className="flex justify-between font-medium border-t pt-1 mt-1">
                  <span className="text-slate-500">Total Allowances</span>
                  <span>{formatMoney(slipDetail.allowances)}</span>
                </div>
                <div className="flex justify-between text-red-600">
                  <span>Leave Deduction</span>
                  <span>-{formatMoney(slipDetail.leaveDeduction)}</span>
                </div>
                {(slipDetail.attendanceDeduction ?? 0) > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Attendance Deduction (Absent/Half-day)</span>
                    <span>-{formatMoney(slipDetail.attendanceDeduction)}</span>
                  </div>
                )}
                {slipDetail.breakdown?.deductionDetails?.length > 0 ? (
                  slipDetail.breakdown.deductionDetails.map((d, i) => (
                    <div key={i} className="flex justify-between text-red-600">
                      <span>{d.headName} ({d.code})</span>
                      <span>-{formatMoney(d.amount)}</span>
                    </div>
                  ))
                ) : null}
                {(slipDetail.otherDeductions ?? 0) > 0 && (
                  <div className="flex justify-between text-red-600">
                    <span>Other Deductions</span>
                    <span>-{formatMoney(slipDetail.otherDeductions)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold border-t pt-2 text-[#0263cc]">
                  <span>Net Salary</span>
                  <span>{formatMoney(slipDetail.netSalary)}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <span className="text-slate-500">Status:</span>
                <Badge className={statusVariant(slipDetail.status)}>{slipDetail.status}</Badge>
                {slipDetail.status === "draft" && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => updateStatus(slipDetail._id, "locked")} disabled={statusLoading}>
                      Lock
                    </Button>
                    <Button size="sm" variant="outline" className="text-emerald-600" onClick={() => updateStatus(slipDetail._id, "paid")} disabled={statusLoading}>
                      Mark Paid
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
